// LeadDetailPage.tsx - Lead detail page with full info, matches, and activity
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Title,
  Text,
  Stack,
  Group,
  Button,
  Paper,
  Badge,
  Divider,
  Tabs,
  Grid,
  Card,
  Table,
  ActionIcon,
  Tooltip,
} from '@mantine/core';
import {
  IconArrowLeft,
  IconEdit,
  IconTrash,
  IconCar,
  IconUser,
  IconMail,
  IconPhone,
  IconCalendar,
  IconChartBar,
} from '@tabler/icons-react';
import { useLead, useUpdateLead, useDeleteLead, useBestMatch } from '@/hooks/useLeads';
import { LeadMatchingPanel } from '@/components/leads/LeadMatchingPanel';
import { ROUTES } from '@/constants/routes';
import { notifications } from '@mantine/notifications';

export function LeadDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: leadResponse, isLoading } = useLead(id || '');
  const { data: bestMatch } = useBestMatch(id || '');
  const updateLead = useUpdateLead();
  const deleteLead = useDeleteLead();

  const lead = leadResponse?.data;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new':
        return 'blue';
      case 'contacted':
        return 'cyan';
      case 'qualified':
        return 'green';
      case 'converted':
        return 'teal';
      case 'lost':
        return 'red';
      case 'archived':
        return 'gray';
      default:
        return 'gray';
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const formatCurrency = (amount?: number) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  const handleDelete = async () => {
    if (!id) return;
    if (window.confirm('Are you sure you want to delete this lead?')) {
      try {
        await deleteLead.mutateAsync(id);
        notifications.show({
          title: 'Success',
          message: 'Lead deleted successfully',
          color: 'green',
        });
        navigate(ROUTES.LEADS);
      } catch (error) {
        // Error handled by mutation
      }
    }
  };

  if (isLoading) {
    return (
      <Container size="xl" py="xl">
        <Text>Loading lead...</Text>
      </Container>
    );
  }

  if (!lead) {
    return (
      <Container size="xl" py="xl">
        <Text>Lead not found</Text>
        <Button mt="md" onClick={() => navigate(ROUTES.LEADS)}>
          Back to Leads
        </Button>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <Group>
            <ActionIcon variant="subtle" onClick={() => navigate(ROUTES.LEADS)}>
              <IconArrowLeft size={20} />
            </ActionIcon>
            <div>
              <Title order={1}>{lead.customer_name}</Title>
              <Text c="dimmed" mt="xs">
                Lead Details
              </Text>
            </div>
          </Group>
          <Group>
            <Button variant="outline" leftSection={<IconEdit size={16} />}>
              Edit
            </Button>
            <Button color="red" leftSection={<IconTrash size={16} />} onClick={handleDelete}>
              Delete
            </Button>
          </Group>
        </Group>

        {/* Quick Stats */}
        <Grid>
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <Card p="md" radius="md" withBorder>
              <Stack gap="xs">
                <Text size="xs" c="dimmed">
                  Status
                </Text>
                <Badge color={getStatusColor(lead.status)} size="lg">
                  {lead.status}
                </Badge>
              </Stack>
            </Card>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <Card p="md" radius="md" withBorder>
              <Stack gap="xs">
                <Text size="xs" c="dimmed">
                  Score
                </Text>
                <Badge
                  color={lead.score >= 70 ? 'green' : lead.score >= 50 ? 'yellow' : 'red'}
                  size="lg"
                >
                  {lead.score}
                </Badge>
              </Stack>
            </Card>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <Card p="md" radius="md" withBorder>
              <Stack gap="xs">
                <Text size="xs" c="dimmed">
                  Budget
                </Text>
                <Text fw={600} size="lg">
                  {formatCurrency(lead.budget)}
                </Text>
              </Stack>
            </Card>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <Card p="md" radius="md" withBorder>
              <Stack gap="xs">
                <Text size="xs" c="dimmed">
                  Source
                </Text>
                <Badge variant="light" size="lg">
                  {lead.source}
                </Badge>
              </Stack>
            </Card>
          </Grid.Col>
        </Grid>

        {/* Main Content */}
        <Tabs defaultValue="details">
          <Tabs.List>
            <Tabs.Tab value="details" leftSection={<IconUser size={16} />}>
              Details
            </Tabs.Tab>
            <Tabs.Tab value="matches" leftSection={<IconCar size={16} />}>
              Matches
            </Tabs.Tab>
            <Tabs.Tab value="activity" leftSection={<IconChartBar size={16} />}>
              Activity
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="details" pt="xl">
            <Grid>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Paper p="md" radius="md" withBorder>
                  <Stack gap="md">
                    <Title order={3}>Contact Information</Title>
                    <Divider />
                    <Group>
                      <IconMail size={20} />
                      <div>
                        <Text size="xs" c="dimmed">
                          Email
                        </Text>
                        <Text>{lead.email || 'N/A'}</Text>
                      </div>
                    </Group>
                    {lead.phone && (
                      <Group>
                        <IconPhone size={20} />
                        <div>
                          <Text size="xs" c="dimmed">
                            Phone
                          </Text>
                          <Text>{lead.phone}</Text>
                        </div>
                      </Group>
                    )}
                  </Stack>
                </Paper>
              </Grid.Col>

              <Grid.Col span={{ base: 12, md: 6 }}>
                <Paper p="md" radius="md" withBorder>
                  <Stack gap="md">
                    <Title order={3}>Car Interest</Title>
                    <Divider />
                    <div>
                      <Text size="xs" c="dimmed">
                        Interest
                      </Text>
                      <Text>{lead.car_interest || 'N/A'}</Text>
                    </div>
                    <div>
                      <Text size="xs" c="dimmed">
                        Budget
                      </Text>
                      <Text>{formatCurrency(lead.budget)}</Text>
                    </div>
                    <div>
                      <Text size="xs" c="dimmed">
                        Timeline
                      </Text>
                      <Text>{lead.timeline || 'N/A'}</Text>
                    </div>
                  </Stack>
                </Paper>
              </Grid.Col>

              {lead.message && (
                <Grid.Col span={12}>
                  <Paper p="md" radius="md" withBorder>
                    <Stack gap="md">
                      <Title order={3}>Message</Title>
                      <Divider />
                      <Text>{lead.message}</Text>
                    </Stack>
                  </Paper>
                </Grid.Col>
              )}

              <Grid.Col span={12}>
                <Paper p="md" radius="md" withBorder>
                  <Stack gap="md">
                    <Title order={3}>Timeline</Title>
                    <Divider />
                    <Table>
                      <Table.Tbody>
                        <Table.Tr>
                          <Table.Td>
                            <Text size="sm" c="dimmed">
                              Created
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">{formatDate(lead.created_at)}</Text>
                          </Table.Td>
                        </Table.Tr>
                        {lead.contacted_at && (
                          <Table.Tr>
                            <Table.Td>
                              <Text size="sm" c="dimmed">
                                Contacted
                              </Text>
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm">{formatDate(lead.contacted_at)}</Text>
                            </Table.Td>
                          </Table.Tr>
                        )}
                        {lead.qualified_at && (
                          <Table.Tr>
                            <Table.Td>
                              <Text size="sm" c="dimmed">
                                Qualified
                              </Text>
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm">{formatDate(lead.qualified_at)}</Text>
                            </Table.Td>
                          </Table.Tr>
                        )}
                        {lead.converted_at && (
                          <Table.Tr>
                            <Table.Td>
                              <Text size="sm" c="dimmed">
                                Converted
                              </Text>
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm">{formatDate(lead.converted_at)}</Text>
                            </Table.Td>
                          </Table.Tr>
                        )}
                        <Table.Tr>
                          <Table.Td>
                            <Text size="sm" c="dimmed">
                              Last Updated
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">{formatDate(lead.updated_at)}</Text>
                          </Table.Td>
                        </Table.Tr>
                      </Table.Tbody>
                    </Table>
                  </Stack>
                </Paper>
              </Grid.Col>
            </Grid>
          </Tabs.Panel>

          <Tabs.Panel value="matches" pt="xl">
            {lead && <LeadMatchingPanel lead={lead} />}
          </Tabs.Panel>

          <Tabs.Panel value="activity" pt="xl">
            <Paper p="md" radius="md" withBorder>
              <Text c="dimmed">Activity feed coming soon...</Text>
            </Paper>
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Container>
  );
}
